import { Capacitor } from "@capacitor/core";
import { shouldReport } from "@/lib/error-report-filter";

/**
 * Deliberately dumb: no queue, no retry, no batching. A report that fails to
 * send is lost, which is correct — the next occurrence sends another, and this
 * code runs on a page that is already in trouble.
 *
 * The caps live here rather than in the listeners so that the window handlers
 * and both error boundaries share one budget per page load.
 */
const MAX_REPORTS_PER_PAGE = 10;

const sent = new Set<string>();
let reported = 0;

export function reportClientError(input: {
  name: string;
  message: string;
  stack?: string | null;
}): void {
  try {
    if (typeof window === "undefined") return;
    if (!shouldReport(input)) return;

    // Cheap local dedupe. The server fingerprints properly; this only stops one
    // page load from spending its ten slots on one repeating error.
    const key = `${input.name}:${input.message}:${(input.stack ?? "").split("\n")[1] ?? ""}`;
    if (sent.has(key) || reported >= MAX_REPORTS_PER_PAGE) return;
    sent.add(key);
    reported += 1;

    // Authoritative, and works on every installed build — unlike the server's
    // user-agent check, which only sees the native token in builds shipped
    // after the appendUserAgent change.
    const platform = Capacitor.getPlatform();

    void fetch("/api/client-error", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        name: input.name,
        message: input.message,
        stack: input.stack ?? undefined,
        platform: platform === "android" ? "ANDROID" : platform === "ios" ? "IOS" : "WEB",
        // Pathname only: the query string is never sent, and the endpoint
        // strips it again on arrival.
        path: window.location.pathname,
      }),
      // The page may be navigating away from the error that just happened.
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Reporting must never be the thing that breaks the page.
  }
}
